import {create} from 'zustand';
import {v4 as uuidv4} from 'uuid';

// Type definitions
export type AmountCalculationType = 'quantity' | 'volume' | 'area' | 'simple';

export interface AmountParameters {
  amount_type: AmountCalculationType;
  unit?: string;
  unit_price?: number;
  quantity?: number;
  width?: number;
  length?: number;
  height?: number;
  overhead?: number; // New field for overhead percentage
  [key: string]: number | string | undefined | AmountCalculationType;
}

export interface ExpenseItem {
  name: string;
  description?: string;
  amount: number;
  expense_date: string;
  receipt_no: string;
}

export interface EstimateItem {
  id: string;
  remote_id?: string;
  name: string;
  description?: string;
  type: 'category' | 'item';
  children?: EstimateItem[];
  amount: number;
  budget_amount: number; // New field for budget amount
  actual_expenses: ExpenseItem[];
  amount_parameters: AmountParameters;
}

interface StoreState {
  items: EstimateItem[];

  addItem: (parentId: string | null, newItem: Pick<EstimateItem, 'name' | 'type'>) => void;
  updateItem: (
    itemId: string,
    updatedData: Partial<Omit<EstimateItem, 'id' | 'type' | 'children'>>,
  ) => void;
  deleteItem: (itemId: string) => void;
  updateTree: (items: EstimateItem[]) => void;
  isLocked: boolean;
  toggleLock: () => void;
}

// Amount calculation functions
const calculateAmount = {
  quantity: (params: AmountParameters) => (params.unit_price || 0) * (params.quantity || 0),
  volume: (params: AmountParameters) => 
    (params.width || 0) * (params.length || 0) * (params.height || 0) * (params.unit_price || 0),
  area: (params: AmountParameters) => 
    (params.width || 0) * (params.length || 0) * (params.unit_price || 0),
  simple: (params: AmountParameters) => params.unit_price || 0,
};

export const stringifyAmountParameter = (params: AmountParameters): string => {
  switch (params.amount_type) {
    case 'quantity':
      return `(SLxGiá): ${params?.quantity || 0}×${params?.unit_price || 0}`;
    case 'volume':
      return `(DxRxCxGiá): ${params?.width || 0}×${params?.length || 0}×${params?.height || 0}×${params?.unit_price || 0}`;
    case 'area':
      return `(DxRxGiá): ${params?.width || 0}×${params?.length || 0}×${params?.unit_price || 0}`;
    case 'simple':
      return `(Giá): ${params?.unit_price || 0}`;
    default:
      return '';
  }
};

// Helper function to find an item by ID
const findItem = (items: EstimateItem[], itemId: string): EstimateItem | null => {
  for (const item of items) {
    if (item.id === itemId) return item;
    if (item.children) {
      const found = findItem(item.children, itemId);
      if (found) return found;
    }
  }
  return null;
};

// Helper function to calculate amount for an item
const calculateItemAmount = (item: EstimateItem): number => {
  if (item.type === 'item') {
    const calculate = calculateAmount[item.amount_parameters.amount_type] || calculateAmount.quantity;
    return calculate(item.amount_parameters);
  }
  return item.children?.reduce((sum, child) => sum + calculateItemAmount(child), 0) || 0;
};

// Helper function to calculate budget amount for an item
const calculateItemBudgetAmount = (item: EstimateItem): number => {
  const baseAmount = calculateItemAmount(item);
  const overhead = item.amount_parameters?.overhead || 0; // Use overhead from amount parameters
  return baseAmount + baseAmount * (overhead / 100);
};

// Helper function to update amounts and budget amounts in a tree
const updateAmounts = (items: EstimateItem[]): void => {
  items.forEach(item => {
    if (item.children) {
      updateAmounts(item.children);
    }
    item.amount = calculateItemAmount(item);
    item.budget_amount = calculateItemBudgetAmount(item);
  });
};

// Mockup data
export const mockupData: EstimateItem[] = [
  {
    id: '1',
    name: 'Chuẩn Bị Mặt Bằng',
    description: 'Công tác chuẩn bị và khởi công',
    type: 'category',
    amount: 0,
    budget_amount: 0,
    actual_expenses: [],
    amount_parameters: { amount_type: 'quantity' },
    children: [
      {
        id: '1-1',
        name: 'Công Tác Đất',
        type: 'category',
        amount: 0,
        budget_amount: 0,
        actual_expenses: [],
        amount_parameters: { amount_type: 'quantity' },
        children: [
          {
            id: '1-1-1',
            name: 'Đào Đất',
            type: 'item',
            amount: 5000,
            budget_amount: 0,
            actual_expenses: [],
            amount_parameters: { amount_type: 'simple', unit_price: 5000},
          },
          {
            id: '1-1-2',
            name: 'San Lấp Mặt Bằng',
            type: 'item',
            amount: 5000,
            budget_amount: 0,
            actual_expenses: [],
            amount_parameters: { amount_type: 'quantity', unit: 'm²', unit_price: 25, quantity: 200 },
          },
        ],
      },
      {
        id: '1-a', 
        name: 'Dọn Dẹp Mặt Bằng', 
        type: 'item',
        amount: 3000, 
        budget_amount: 0,
        actual_expenses: [], 
        amount_parameters: { amount_type: 'quantity', unit: 'lô', unit_price: 3000, quantity: 1 }
      },
      {
        id: '1-b',
        name: 'Hàng Rào Tạm',
        type: 'item',
        amount: 4500,
        budget_amount: 0,
        actual_expenses: [],
        amount_parameters: { amount_type: 'quantity', unit: 'md', unit_price: 45, quantity: 100 },
      },
    ],
  },
  {
    id: '2',
    name: 'Móng',
    description: 'Công tác móng',
    type: 'category',
    amount: 0,
    budget_amount: 0,
    actual_expenses: [],
    amount_parameters: { amount_type: 'quantity' },
    children: [
      {
        id: '2-a', 
        name: 'Đổ Bê Tông', 
        type: 'item', 
        amount: 10000, 
        budget_amount: 0,
        actual_expenses: [], 
        amount_parameters: { amount_type: 'quantity', unit: 'm³', unit_price: 200, quantity: 50 }
      },
      {
        id: '2-b', 
        name: 'Lắp Đặt Cốt Thép', 
        type: 'item', 
        amount: 6000, 
        budget_amount: 0,
        actual_expenses: [], 
        amount_parameters: { amount_type: 'quantity', unit: 'tấn', unit_price: 1200, quantity: 5 }
      },
    ],
  },
];

// Update initial amounts with overhead
updateAmounts(mockupData); // Example overhead of 10%

// Zustand store
const useStore = create<StoreState>(set => ({
  items: [],

  addItem: (parentId, newItem) =>
    set(state => {
      const itemWithAmount = {
        id: uuidv4(),
        ...newItem,
        amount: 0,
        budget_amount: 0,
        actual_expenses: [],
        amount_parameters: {
          amount_type: 'quantity' as AmountCalculationType,
        },
        children: newItem.type === 'category' ? [] : undefined,
      };

      if (!parentId) {
        const newItems = [...state.items, itemWithAmount];
        updateAmounts(newItems);
        return {items: newItems};
      }

      const updatedItems = [...state.items];
      const parent = findItem(updatedItems, parentId);
      if (parent && parent.type === 'category') {
        if (!parent.children) parent.children = [];
        parent.children.push(itemWithAmount);
        updateAmounts(updatedItems);
      }
      return {items: updatedItems};
    }),

  updateTree: (items) =>
    set(() => {
      const updatedItems = [...items];
      updateAmounts(updatedItems);
      return { items: updatedItems };
    }),

  updateItem: (itemId, updatedData) =>
    set(state => {
      // Special case for root update (used after drag-n-drop)
      if (itemId === 'root' && 'children' in updatedData) {
        const newItems = updatedData.children as EstimateItem[];
        updateAmounts(newItems);
        return {items: newItems};
      }

      const updatedItems = [...state.items];
      const item = findItem(updatedItems, itemId);
      if (item) {
        // Update item with new data
        Object.assign(item, updatedData);
        // Recalculate amount if parameters changed
        if ('amount_parameters' in updatedData) {
          item.amount = calculateItemAmount(item);
        }
        updateAmounts(updatedItems);
      }
      return {items: updatedItems};
    }),

  deleteItem: itemId =>
    set(state => {
      const removeItem = (items: EstimateItem[]): EstimateItem[] =>
        items.filter(item => {
          if (item.id === itemId) return false;
          if (item.children) {
            item.children = removeItem(item.children);
          }
          return true;
        });

      return {items: removeItem(state.items)};
    }),
  isLocked: true,
  toggleLock: () => set(state => ({ isLocked: !state.isLocked })),
}));

export default useStore;
